import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import type { InternationalReport } from '../lib/types';

/**
 * Ülke ve ihracat raporu (F7-20): teslimat ülkesi ve para birimi bazında sipariş, ihracat, brüt, KDV, kargo, iade, net
 * ve siparişe yazılan kurla TL karşılığı. Farklı para birimleri toplanmaz; genel toplam yalnızca TL karşılığıdır.
 */

function istanbulToday() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul' }).format(new Date()).slice(0, 10);
}

const th = 'px-3 py-2 text-start font-medium';
const thR = 'px-3 py-2 text-end font-medium';
const td = 'px-3 py-2';
const tdR = 'px-3 py-2 text-end tabular';

export function InternationalReportPage() {
  const t = useT();
  const money = useMoney();
  const { stores } = useStoreChoice();
  const today = istanbulToday();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [storeId, setStoreId] = useState('');
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (storeId) params.set('storeId', storeId);
  const path = '/v1/admin/reports/international';
  const q = useQuery({
    queryKey: ['international-report', params.toString()],
    queryFn: () => api.get<InternationalReport>(`${path}?${params}`),
  });
  const download = async () => {
    try {
      const { blob, fileName } = await api.blob(`${path}?${params}&format=csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'ulke-satis-raporu.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    }
  };
  const data = q.data;
  return (
    <PageFrame
      helpKey="page.international_report"
      actions={
        <span className="inline-flex items-center gap-1">
          <Button onClick={() => void download()}>{t('admin.internationalReport.csv')}</Button>
          <HelpTip k="international_report.csv" />
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.internationalReport.from')} help={<HelpTip k="international_report.period" />}>
          {(p) => (
            <Input {...p} type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          )}
        </Field>
        <Field label={t('admin.internationalReport.to')}>
          {(p) => <Input {...p} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />}
        </Field>
        {stores.length > 1 && (
          <Field
            label={t('admin.internationalReport.store')}
            help={<HelpTip k="international_report.store" />}
          >
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">{t('admin.internationalReport.allStores')}</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </div>
      <p className="max-w-[80ch] text-sm text-ink-3">{t('admin.internationalReport.note')}</p>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !data ? (
        <Spinner label={t('common.loading')} />
      ) : !data.rows.length ? (
        <EmptyState
          title={t('admin.internationalReport.empty')}
          description={t('admin.internationalReport.emptyLead')}
        />
      ) : (
        <>
          <dl className="grid max-w-3xl grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
            <div>
              <dt className="text-sm text-ink-3">{t('admin.internationalReport.totalOrders')}</dt>
              <dd className="tabular text-lg font-medium">{data.tryTotal.orders}</dd>
            </div>
            <div>
              <dt className="inline-flex items-center gap-1 text-sm text-ink-3">
                {t('admin.internationalReport.tryNet')}
                <HelpTip k="international_report.try_total" />
              </dt>
              <dd className="tabular text-lg font-medium">{money(data.tryTotal.netMinor, 'TRY')}</dd>
            </div>
            <div>
              <dt className="text-sm text-ink-3">{t('admin.internationalReport.exportOrders')}</dt>
              <dd className="tabular text-lg font-medium">{data.tryTotal.exportOrders}</dd>
            </div>
            <div>
              <dt className="text-sm text-ink-3">{t('admin.internationalReport.exportTryNet')}</dt>
              <dd className="tabular text-lg font-medium">{money(data.tryTotal.exportNetMinor, 'TRY')}</dd>
            </div>
          </dl>
          {data.tryTotal.ordersWithoutRate > 0 && (
            <p role="status" className="max-w-[80ch] text-sm text-amber">
              {t('admin.internationalReport.withoutRate', { count: data.tryTotal.ordersWithoutRate })}
            </p>
          )}
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[60rem] text-sm">
              <caption className="sr-only">{t('admin.internationalReport.caption')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th scope="col" className={th}>
                    {t('admin.internationalReport.country')}
                  </th>
                  <th scope="col" className={th}>
                    {t('admin.internationalReport.currency')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.orders')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.exportOrders')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.gross')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.tax')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.shipping')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.refunded')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.net')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.internationalReport.tryNet')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={`${r.countryCode ?? '—'}-${r.currency}`}
                    className="border-t border-line align-top"
                  >
                    <th scope="row" className={`${td} font-medium`}>
                      {r.countryName ?? (
                        <span className="font-normal text-ink-3">
                          {t('admin.internationalReport.unknown')}
                        </span>
                      )}
                      {r.countryCode && <span className="font-normal text-ink-3"> ({r.countryCode})</span>}
                    </th>
                    <td className={td}>{r.currency}</td>
                    <td className={tdR}>{r.orders}</td>
                    <td className={tdR}>{r.exportOrders || '—'}</td>
                    <td className={tdR}>{money(r.grossMinor, r.currency)}</td>
                    <td className={tdR}>{money(r.taxMinor, r.currency)}</td>
                    <td className={tdR}>{money(r.shippingMinor, r.currency)}</td>
                    <td className={tdR}>{r.refundedMinor ? money(r.refundedMinor, r.currency) : '—'}</td>
                    <td className={tdR}>{money(r.netMinor, r.currency)}</td>
                    <td className={tdR}>
                      {money(r.tryNetMinor, 'TRY')}
                      {r.ordersWithoutRate > 0 && (
                        <span className="block text-amber">
                          {t('admin.internationalReport.rowWithoutRate', { count: r.ordersWithoutRate })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-line bg-surface-2 font-medium">
                {data.currencies.map((c) => (
                  <tr key={c.currency}>
                    <th scope="row" className={td}>
                      {t('admin.internationalReport.currencyTotal')}
                    </th>
                    <td className={td}>{c.currency}</td>
                    <td className={tdR}>{c.orders}</td>
                    <td className={tdR}>{c.exportOrders || '—'}</td>
                    <td className={tdR}>{money(c.grossMinor, c.currency)}</td>
                    <td className={tdR}>{money(c.taxMinor, c.currency)}</td>
                    <td className={tdR}>{money(c.shippingMinor, c.currency)}</td>
                    <td className={tdR}>{c.refundedMinor ? money(c.refundedMinor, c.currency) : '—'}</td>
                    <td className={tdR}>{money(c.netMinor, c.currency)}</td>
                    <td className={tdR}>{money(c.tryNetMinor, 'TRY')}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        </>
      )}
    </PageFrame>
  );
}
